import type { SiteTopology } from "@/data/types";

/**
 * Bus-centric SCADA layout — the EMS isn't a passthrough node, it's just
 * the controller that watches the bus. Energy flows between the four
 * physical actors (grid, PV, BESS, facility) and is wired directly to
 * the common AC bus that links them:
 *
 *   ┌─────────────────────────────────────────────────────────────────┐
 *   │  ┌─────┐                ☀ PV                       ┌─────────┐ │
 *   │  │Grid │ ◀──────━━━━━━━━━━━●━━━━━━━━━━━●━━━━━━━━━━▶│ Factory │ │
 *   │  └─────┘                   │           │           └─────────┘ │
 *   │     ▲ (front-of-meter)     │  ▣ EMS    │ (behind-the-meter)    │
 *   │                            ▼           ▼                        │
 *   │                          ▮▮▮▮ BESS fleet ▮▮▮▮                  │
 *   └─────────────────────────────────────────────────────────────────┘
 *
 * - The horizontal bus is the visual spine.
 * - PV taps the bus from above; BESS taps from below; grid + load tap
 *   from the sides.
 * - The EMS hub is a small "controller chip" sitting ON the bus with a
 *   dashed control line to the BESS — it does NOT carry kilowatts.
 *
 * Adaptive rules (BESS row sizing identical to v1 — we re-use the
 * existing per-cabinet rendering):
 *   1   → single cabinet centred under the bus
 *   2-4 → row
 *   5+  → multi-row grid (auto)
 *
 * Geometry uses a fixed-width 1200px viewBox; the height grows with
 * the BESS row count so multi-row fleets don't get their per-unit
 * labels clipped below the SVG.
 */
export const VB_WIDTH = 1200;
export const VB_HEIGHT_COMPACT = 520;
export const VB_HEIGHT_FULL = 620;

export type ScadaVariant = "compact" | "full";

export interface Port { x: number; y: number; }
/** Stable, locale-agnostic identifier for each SCADA zone. The display
 *  string is resolved from the i18n catalog at render time (ZoneBackdrop)
 *  keyed off this id — buildLayout() never bakes a localized label in. */
export type ZoneId = "frontOfMeter" | "onSite" | "behindMeter";
export interface Zone {
  x: number; y: number; w: number; h: number;
  id: ZoneId;
  centerPort: Port;
  leftPort: Port;
  rightPort: Port;
  topPort: Port;
  bottomPort: Port;
}
export interface BessGrid {
  rows: number;
  cols: number;
  cellW: number;
  cellH: number;
  origin: { x: number; y: number };
  totalW: number;
  totalH: number;
  /** Indices of cabinets that fit in the grid (overflow gets a +N chip). */
  visibleCount: number;
  overflowCount: number;
  /** Cabinet body dimensions inside each grid cell. */
  cabW: number;
  cabH: number;
  /** Where the trunk lines up vertically: x of the centre of the fleet. */
  cx: number;
}

export interface PvNode {
  cx: number;
  cy: number;
  /** Operator-facing label, e.g. "PV-1". Omitted on single-array sites. */
  label?: string;
  /** Per-node rated kWp from topology (when known). */
  rated_kWp?: number;
  /** Stable id (matches the underlying PV smartmeter / asset id). */
  id?: string;
}

export interface ScadaLayout {
  width: number;
  height: number;
  bus: {
    x1: number;     // bus left endpoint (grid tap)
    x2: number;     // bus right endpoint (load tap)
    y: number;
    /** Tap points along the bus for each non-edge node. */
    pvTapX: number;    // where the PV downcomer joins the bus (single PV)
    bessTapX: number;  // where the BESS trunk meets the bus
    hubX: number;      // where the EMS controller chip sits on the bus
  };
  busVoltage: string;
  zones: {
    frontOfMeter: Zone;
    onSite: Zone;
    behindMeter: Zone;
  };
  /** The EMS chip — small, lives ON the bus near the BESS trunk. */
  hub: { cx: number; cy: number; r: number };
  grid: { cx: number; cy: number };
  factory: { cx: number; cy: number };
  /** Zero, one, or N PV nodes laid out above the bus. */
  pv: PvNode[];
  bessGrid: BessGrid;
}

/** Vertical reserve above the bus, in viewBox units. Must accommodate
 *  the PV plate (max ~100 high, centred at busY - 110) and the
 *  grid/factory plates (centred on busY, ±60). */
const ABOVE_BUS = 200;
/** Vertical reserve between the bus and the top of the BESS fleet. */
const TRUNK_GAP = 70;
/** Reserve below the BESS fleet for the fleet summary chip + zone
 *  padding. Per-unit footer labels are already accounted for in cellH. */
const SUMMARY_PAD = 80;

export function buildLayout(topology: SiteTopology, variant: ScadaVariant): ScadaLayout {
  const pvMeters = topology.smartmeters.filter((m) => m.role === "pv");
  const aggregatePv = !pvMeters.length && Boolean(topology.aggregation.pv_power_W);
  const pvCount = pvMeters.length || (aggregatePv ? 1 : 0);
  const w = VB_WIDTH;

  // 1) Decide the BESS grid shape so we know how many rows of cabinets
  //    we'll be laying out.
  const bessCount = topology.bess.length;
  const { rows, cols } = bessGridShape(bessCount);
  const visibleCount = rows * cols;
  const overflowCount = Math.max(0, bessCount - visibleCount);
  const idealCellW = bessCount === 1 ? 140
    : cols <= 2 ? 130
    : cols <= 4 ? 118
    : cols <= 6 ? 102
    : 86;
  // Cabinet body height (the rectangle), separate from cell spacing.
  // Multi-row fleets get slightly shorter cabinets to keep the overall
  // diagram compact, but every row leaves enough space below its
  // cabinets for the 3-line footer label.
  const cabH = rows === 1 ? 160
    : rows === 2 ? 128
    : 108;
  const labelStack = 48;
  const cellH = cabH + labelStack;
  const rowGap = rows > 1 ? 4 : 0;
  const totalH = rows * cellH + (rows - 1) * rowGap;

  // 2) Derive the SVG height from the content. The bus sits a fixed
  //    distance below the top so the grid/PV/factory plates always
  //    fit; everything below — BESS rows, labels, summary — stretches
  //    the canvas as needed.
  const busY = ABOVE_BUS;
  const baseH = variant === "full" ? VB_HEIGHT_FULL : VB_HEIGHT_COMPACT;
  const requiredH = busY + TRUNK_GAP + totalH + SUMMARY_PAD;
  const h = Math.max(baseH, requiredH);

  // 3) Zones span the full canvas height.
  const zoneGap = 8;
  const zoneY = 12;
  const zoneH = h - 24;
  const fmW = (w - zoneGap * 2) * 0.22;
  const onW = (w - zoneGap * 2) * 0.56;
  const bmW = (w - zoneGap * 2) * 0.22;
  const fmZone: Zone = makeZone(0, zoneY, fmW, zoneH, "frontOfMeter");
  const onZone: Zone = makeZone(fmW + zoneGap, zoneY, onW, zoneH, "onSite");
  const bmZone: Zone = makeZone(fmW + onW + zoneGap * 2, zoneY, bmW, zoneH, "behindMeter");

  // 4) Bus geometry. Taps land at the far edge of each side zone so
  //    the flow line + label sit clearly in the open gap.
  const busX1 = fmZone.x + fmZone.w - 8;
  const busX2 = bmZone.x + 8;

  // 5) Grid plate sits in the LEFT half of the front-of-meter zone so
  //    a long horizontal flow line (and its label) has room to breathe
  //    to the right of it.
  const grid = { cx: fmZone.x + 86, cy: busY };
  // Factory plate mirrors that, in the RIGHT half of the BM zone.
  const factory = { cx: bmZone.x + bmZone.w - 86, cy: busY };

  // 6) Fleet horizontal extent — centred in the on-site zone.
  const fleetMaxW = onZone.w - 80;
  const cellGap = cols > 6 ? 8 : 14;
  const cellW = Math.max(64, Math.min(idealCellW, Math.floor((fleetMaxW - (cols - 1) * cellGap) / cols)));
  const totalW = cols * cellW + (cols - 1) * cellGap;
  const fleetCx = onZone.x + onZone.w / 2;
  const originX = fleetCx - totalW / 2;
  const originY = busY + TRUNK_GAP;

  const bessGrid: BessGrid = {
    rows, cols, cellW, cellH,
    origin: { x: originX, y: originY },
    totalW, totalH,
    visibleCount: Math.min(bessCount, visibleCount),
    overflowCount,
    cabW: Math.max(60, cellW - 8),
    cabH,
    cx: fleetCx,
  };

  // 7) PV row — above the bus, in the LEFT half of the on-site zone
  // (so the BESS trunk + its right-side label own the right half).
  // PV-array plates are 128 wide; fitting more than two side-by-side
  // pushes them into the BESS trunk, so when there are ≥ 3 arrays we
  // render a SINGLE "N× PV" aggregate node instead of cluttering the
  // strip with overlapping panels (per-array telemetry isn't surfaced
  // in this view anyway — every array shares the aggregate kW reading).
  const pv: PvNode[] = [];
  if (pvCount > 0) {
    const pvY = busY - 110;
    if (pvCount === 1) {
      pv.push({
        cx: fleetCx - 220,
        cy: pvY,
        id: pvMeters[0]?.id,
      });
    } else if (pvCount === 2) {
      // Two distinct PV plates spaced wide enough that neither plate
      // intrudes on the other's flow-label pill (label sits to the
      // right of each downcomer, plates are 128 wide).
      const centre = fleetCx - 200;
      const spacing = 220;
      for (let i = 0; i < 2; i++) {
        pv.push({
          cx: centre + (i - 0.5) * spacing,
          cy: pvY,
          label: `PV-${i + 1}`,
          id: pvMeters[i]?.id,
        });
      }
    } else {
      // 3+ PV arrays — render as one aggregate node with a `N× PV` tag.
      pv.push({
        cx: fleetCx - 220,
        cy: pvY,
        label: `${pvCount}× PV`,
        id: pvMeters[0]?.id,
      });
    }
  }

  // 8) EMS chip — sits ON the bus, to the LEFT of the BESS trunk so
  //    the BESS power label (right of trunk) doesn't collide with it.
  const hubX = fleetCx - 110;
  const hub = { cx: hubX, cy: busY, r: 20 };

  return {
    width: w,
    height: h,
    bus: {
      x1: busX1, x2: busX2, y: busY,
      pvTapX: pvCount > 0 ? pv[pv.length - 1].cx : fleetCx,
      bessTapX: fleetCx,
      hubX,
    },
    busVoltage: "400 V · 50 Hz",
    zones: { frontOfMeter: fmZone, onSite: onZone, behindMeter: bmZone },
    hub,
    grid,
    factory,
    pv,
    bessGrid,
  };
}

function makeZone(x: number, y: number, w: number, h: number, id: ZoneId): Zone {
  return {
    x, y, w, h, id,
    centerPort: { x: x + w / 2, y: y + h / 2 },
    leftPort:   { x, y: y + h / 2 },
    rightPort:  { x: x + w, y: y + h / 2 },
    topPort:    { x: x + w / 2, y },
    bottomPort: { x: x + w / 2, y: y + h },
  };
}

/**
 * Choose a (rows, cols) grid for N BESS cabinets that stays approximately
 * 2.5:1 wide (matches the on-site zone's aspect) regardless of count.
 */
function bessGridShape(n: number): { rows: number; cols: number } {
  if (n <= 1) return { rows: 1, cols: 1 };
  if (n <= 4) return { rows: 1, cols: n };
  if (n <= 16) return { rows: 2, cols: Math.ceil(n / 2) };
  if (n <= 24) return { rows: 3, cols: Math.ceil(n / 3) };
  return { rows: 3, cols: 8 };
}

/**
 * Build a smooth cubic-bezier path between two points with a controllable
 * curvature in the X axis. Used for every flow line — gives the "polished
 * infographic" feel of the reference diagrams.
 */
export function curvedPath(
  from: Port, to: Port,
  opts: { tension?: number; horizontalFirst?: boolean } = {},
): string {
  const tension = opts.tension ?? 0.5;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (opts.horizontalFirst === false || Math.abs(dy) > Math.abs(dx)) {
    // Vertical-first curve
    const c1: Port = { x: from.x, y: from.y + dy * tension };
    const c2: Port = { x: to.x,   y: to.y - dy * tension };
    return `M${from.x},${from.y} C${c1.x},${c1.y} ${c2.x},${c2.y} ${to.x},${to.y}`;
  }
  const c1: Port = { x: from.x + dx * tension, y: from.y };
  const c2: Port = { x: to.x - dx * tension,   y: to.y };
  return `M${from.x},${from.y} C${c1.x},${c1.y} ${c2.x},${c2.y} ${to.x},${to.y}`;
}

export function magnitudeToWidth(watts: number, refKW = 60) {
  const kw = Math.abs(watts) / 1000;
  if (kw < 0.2) return 2;                 // active-but-tiny floor (keeps idle-vs-active legible)
  const ref = Math.max(1, refKW);
  const ratio = Math.min(1, kw / ref);    // clamp so a flow that exceeds nameplate (e.g. grid import) can't re-saturate past the cap
  // 2px floor … 9px cap on a sqrt curve so mid-range flows are clearly separated
  return 2 + Math.sqrt(ratio) * 7;        // ratio 0→2px, 0.25→5.5px, 1→9px
}
